import * as React from 'react'
import { getNumberOnly } from 'app/shared/utils/comUtil'
import InputField from 'app/shared/components/InputField'
import { Button } from 'primereact/button'
import { InputSecureFieldRef, InputSecureFieldReturn } from './InputSecureField'
import { InputBaseProps } from './InputTypes'
import { useValidation } from 'app/shared/hooks/validation/validation.hook'
export type { InputSecureFieldRef }

type InputRnnFieldSecureProps = Pick<
    InputBaseProps,
    'result' | 'resultId' | 'resultMsg'
> & {
    /**
     * 뒷 주민번호 앞1자리를 보여줄지 여부, true 보여줌, false 감춤, default: false
     */
    showGender?: boolean
    /**
     * 주민번호 앞자리
     */
    rnn?: string
    disabled?: boolean
    /**
     * 뒷 주민번호 한자리만 입력여부, true 한자리만 입력, false 전체입력, default: true
     */
     oneRnn2?: boolean
    /**
     * 무엇인가 입력되면 호출된다.
     * @param rnn1 - 주민번호 앞자리
     * @param rnn2 - 주민번호 뒷자리
     */
    onChange?(rnn1: string, rnn2: string): void
    /**
     * 보안 키패드가 닫힐때 호출된다.
     * @param rnn1 - 주민번호 앞자리
     * @param rnn2 - 주민번호 뒷자리
     * @param isFulfilled - 주민번호 뒷자리가 모두 입력됐는지 여부
     */
    onClose?(rnn1: string, rnn2: string, isFulfilled: boolean): void
    onBlur?(): void
}

export const InputRnnFieldSecure = React.forwardRef<
    InputSecureFieldRef,
    InputRnnFieldSecureProps
>((props, ref) => {
    const {
        rnn = '',
        showGender = false,
        disabled = false,
        oneRnn2 = true,
        onChange,
        onClose,
        onBlur,
        result,
        resultId,
        resultMsg,
    } = props
    //주민번호 앞자리
    const [rnn1, setRnn1] = React.useState(rnn)
    //주민번호 앞자리 Ref
    const rnn1Ref = React.useRef(rnn1)
    //주민번호 뒷자리 입력용
    const rnn2InputRef = React.useRef(null)
    //주민번호 뒷자리
    const [rnn2, setRnn2] = React.useState('')
    //주민번호 뒷자리 Ref
    const rnn2Ref = React.useRef(rnn2)
    //주민번호 뒷자리 현재 입력 길이(*** 표시를 위해)
    const [count, setCount] = React.useState(0)
    //핸들러 업데이트를 위한 단순 State
    const [updateHandler, setUpdateHandler] = React.useState(0)

    React.useImperativeHandle(
        ref,
        () => ({
            getSecureData: getSecureData,
            //제거될 수 있음... 사용하는 경우 문의
            getData: () => `${rnn1Ref}-${rnn2Ref}`,
            isFulfilled: () => {
                return rnn2Ref.current.length >= (oneRnn2 ? 1 : 7)
            },
            focus: () => {},
            clear: () => {},
        }),
        []
    )

    React.useEffect(() => {
        //(필수)native input field가 생성된 이후 호출해야함.
        globalThis.initmTranskey()
    }, [])

    React.useEffect(() => {
        //이벤트가 한번에 한개만 등록 가능해서 수정함.
        const mtk = globalThis.mtk
        if (!mtk) return
        mtk.startCallBack = mtk.clearCallBack = mtk.delCallBack = keypadCallback
        mtk.doneCallBack = mtk.closeCallBack = handleClose
        return () => {
            mtk.startCallBack = mtk.clearCallBack = mtk.delCallBack = null
            mtk.doneCallBack = mtk.closeCallBack = null
        }
    }, [updateHandler])

    const keypadCallback = React.useMemo(() => {
        return () => {
            if (!globalThis.mtk.now) return
            if (globalThis.mtk.now.inputObj !== rnn2InputRef.current) return
            updateKeypadValue()
        }
    }, [])
    const updateKeypadValue = React.useCallback(() => {
        const aValue = (rnn2InputRef.current.value as string) ?? ''
        rnn2Ref.current = aValue.replace(/./g, '*')
        setCount(aValue.length)
        setRnn2(aValue)
        onChange?.(rnn1Ref.current, rnn2Ref.current)
    }, [])

    const handleClose = React.useCallback(() => {
        updateKeypadValue()
        onClose?.(rnn1Ref.current, rnn2Ref.current, rnn2Ref.current.length >= (oneRnn2 ? 1: 7))
    }, [])

    const handleRnn1Changed = React.useCallback(
        (e: React.ChangeEvent<HTMLInputElement>) => {
            const aValue = getNumberOnly(e.target.value as string)
            rnn1Ref.current = aValue
            setRnn1(aValue)
            onChange?.(rnn1Ref.current, rnn2Ref.current)

            setTimeout(() => {
                if (aValue.length === 6) {
                    backRrnRef.current?.focus()
                    backRrnRef.current?.click()
                }
            }, 100)
        },
        []
    )

	const handleRnn2Changed = React.useCallback(
	    (e: React.ChangeEvent<HTMLInputElement>) => {
	        const aValue = getNumberOnly(e.target.value as string)
	        rnn2Ref.current = aValue
	        setRnn2(aValue)
	        onChange?.(rnn1Ref.current, rnn2Ref.current)
	    },
	    []
	)

    const backRrnRef = React.useRef<Button & HTMLButtonElement>()

    const getSecureData = (): InputSecureFieldReturn => {
        globalThis.mtk.fillEncData()
		if(oneRnn2){
			return {
				id:'oneRnn2',
				transkeyPwd: rnn2Ref.current,
				transkeyHM: '',
				keyboardType:'',
				keyIndex: '',
				fieldType: '',
				seedKey: '',
				initTime: '',
				extras: rnn1Ref.current,
			}
		}
        return {
            id: 'rnn2',
            transkeyPwd: (
                document.getElementById('transkey_rnn2') as HTMLInputElement
            ).value,
            transkeyHM: (
                document.getElementById('transkey_HM_rnn2') as HTMLInputElement
            ).value,
            keyboardType: (
                document.getElementById('keyboardType_rnn2') as HTMLInputElement
            ).value,
            keyIndex: (
                document.getElementById('keyIndex_rnn2') as HTMLInputElement
            ).value,
            fieldType: (
                document.getElementById('fieldType_rnn2') as HTMLInputElement
            ).value,
            seedKey: (document.getElementById('seedKey') as HTMLInputElement)
                .value,
            initTime: (document.getElementById('initTime') as HTMLInputElement)
                .value,
            extras: rnn1Ref.current,
        }
    }

    const resultCheck = React.useMemo(() => {
        let rt = null
        if (result !== 'hidden' && resultMsg) {
            rt = (
                <p id={resultId} className={`p-msg-${result}`}>
                    {resultMsg}
                </p>
            )
        }
        return rt
    }, [result, resultMsg])

    return (
        <>
            <span className="inputs-label">주민등록번호</span>
            <div
                className={`inputs-x-mix inputs-jumin  ${
                    result === 'error' ? '-error' : ''
                }`}
            >
                <input
                    type={oneRnn2 ? "" : "password"}
                    title="키패드 입력"
                    id="rnn2"
                    name="rnn2"
                    maxLength={oneRnn2 ? 1 : 7}
                    value={rnn2}
                    ref={rnn2InputRef}
                    onChange={(e) => {
                        //단지 lint error로 추가
                    }}
                    data-tk-nextsibling="false"
                    data-tk-kbdtype="number"
                    data-tk-bottom="true"
                    style={{
                        display: 'none',
                    }}
                />
                <InputField
                    inputMode="numeric"
                    type="text"
                    maxLength={6}
                    value={rnn1}
                    onChange={handleRnn1Changed}
                    placeholder="생년월일 6자리"
                    title="주민등록번호 앞자리"
                    onBlur={onBlur}
                    disabled={oneRnn2 ? disabled : !disabled}
                />
                <span className="bar px-10">-</span>
				
				
				{/*휴대폰 본인확인시 처음은 무조건 실명번호 7자리(성별까지만,성별보이기)*/}			   
			   {!oneRnn2 ? (
                <Button
                    ref={backRrnRef}
                    className="second-area"
                    disabled={disabled}
                    onClick={() => {
                        globalThis.mtk.onKeyboard(rnn2InputRef.current)
                        setCount(0)
                        setRnn2('')
                        setUpdateHandler((prev) => prev + 1)
                    }}
                >
                    {showGender && (
                        <InputField
                            value={rnn2.charAt(0)}
                            title="주민등록번호 뒷자리 1번째"
                            className="input-first"
                            readOnly={true}
                        />
                    )}
                    {/* 보안키패드 입력값에 따라 -digit 숫자 변경 */}
                    <div className={`keypad-password-${showGender ? 6 : 7}`}>
                        <div
                            title="주민등록번호 뒷자리"
                            className={`input-btn-keypad -digit${
                                showGender ? count - 1 : count
                            }`}
                        ></div>
                    </div>
                    
                </Button>
				) : (
				<div className="second-area">
					<InputField
						inputMode="numeric"
	                    type="text"
	                    maxLength={1}
	                    value={rnn2}
	                    onChange={handleRnn2Changed}
	                    title="주민등록번호 뒷자리"
	               />
				   <div className="keypad-password-6">
	                   <div
	                       title="주민등록번호 뒷자리"
						className="input-btn-keypad -digit6"
	                   ></div>
	               </div>
				</div>
				)}
            </div>
            {resultCheck}
        </>
    )
})
